import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import { message } from 'antd';
import { escapeHtml, fetchRouteMetrics } from '../../utils/geocoding';
import { api } from '../../services/api';

// Multi-route map used only on the dashboard (shows all vehicles' routes simultaneously).
// Not to be confused with LiveRouteMap.jsx, which is the single-route customer-tracking view.
const DashboardMap = ({ routes }) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const featureGroupRef = useRef(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [routeMetrics, setRouteMetrics] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (routes.length > 0 && mapRef.current && !mapInstance.current) {
      const map = L.map(mapRef.current).setView([routes[0].vehicle.coords[1], routes[0].vehicle.coords[0]], 12);
      mapInstance.current = map;
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a>OpenStreetMaps</a>'
      }).addTo(map);

      const featureGroup = L.featureGroup().addTo(map);
      featureGroupRef.current = featureGroup;

      routes.forEach(async (route, routeIndex) => {
        setIsLoading(true);
        const points = [
          { latitude: route.vehicle.coords[1], longitude: route.vehicle.coords[0] },
          ...route.deliveries.map(d => ({ latitude: d.coords[1], longitude: d.coords[0] })),
        ];

        const metrics = await fetchRouteMetrics(points);
        setRouteMetrics(prev => ({ ...prev, [route.vehicle.name]: metrics }));

        L.marker([route.vehicle.coords[1], route.vehicle.coords[0]], {
          icon: L.icon({ iconUrl: 'https://cdn-icons-png.flaticon.com/512/2838/2838912.png', iconSize: [32, 32], iconAnchor: [16, 32] }),
        }).addTo(featureGroup).bindTooltip(
          `<b>${escapeHtml(route.vehicle.name)}</b><br>Driver: ${escapeHtml(route.vehicle.driver)}<br>Capacity: ${escapeHtml(route.vehicle.capacity)}kg<br>Total Load: ${escapeHtml(route.totalWeight)}kg<br>Stops: ${route.deliveries.length}`,
          { permanent: false, direction: 'top', className: 'custom-tooltip' }
        );

        route.deliveries.forEach((delivery) => {
          L.marker([delivery.coords[1], delivery.coords[0]], {
            icon: L.icon({ iconUrl: 'https://cdn-icons-png.flaticon.com/512/684/684908.png', iconSize: [24, 24], iconAnchor: [12, 24] }),
          }).addTo(featureGroup).bindTooltip(
            `<b>${escapeHtml(delivery.name)}</b><br>Priority: ${escapeHtml(delivery.priority)}<br>Weight: ${escapeHtml(delivery.weight)}kg<br>Time: ${escapeHtml(delivery.timeWindow.start)}:00 - ${escapeHtml(delivery.timeWindow.end)}:00<br><strong>Vehicle:</strong> ${escapeHtml(route.vehicle.name)} (${escapeHtml(route.vehicle.driver)})`,
            { permanent: false, direction: 'top', className: 'custom-tooltip' }
          );
        });

        const path = points.map(p => [p.latitude, p.longitude]);
        const colors = ['blue', 'green', 'purple', 'orange', 'red'];
        L.polyline(path, { color: colors[routeIndex % colors.length], weight: 4, opacity: 0.7 }).addTo(featureGroup).bindTooltip(
          `<b>Route for ${escapeHtml(route.vehicle.name)}</b><br>Total Distance: ${escapeHtml(metrics.distance)} km<br>Total Time: ${Math.floor(route.totalTime)} hrs ${Math.round((route.totalTime % 1) * 60)} mins<br>Stops: ${route.deliveries.length}`,
          { permanent: false, sticky: true, direction: 'center', className: 'custom-tooltip' }
        );
        setIsLoading(false);
      });
      setIsMapReady(true);
    }
  }, [routes]);

  useEffect(() => {
    if (isMapReady && featureGroupRef.current) {
      const checkBounds = setInterval(() => {
        if (featureGroupRef.current.getLayers().length > 0) {
          clearInterval(checkBounds);
          mapInstance.current.fitBounds(featureGroupRef.current.getBounds(), { padding: [50, 50] });
        }
      }, 100);
    }
  }, [isMapReady]);

  return (
    <>
      {isLoading && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', backgroundColor: 'white', padding: '15px 25px', borderRadius: '8px', boxShadow: '0 0 10px rgba(0,0,0,0.2)', zIndex: 1000, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div className="spinner" style={{ width: '20px', height: '20px', border: '3px solid rgba(0,0,0,0.1)', borderTopColor: '#667eea', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
          <span>Loading route data...</span>
        </div>
      )}
      <div ref={mapRef} style={{ height: '600px', width: '100%', position: 'relative' }} />
    </>
  );
};

const DashboardTab = ({ metrics, optimizedRoutes }) => {
  const handleCopyURL = async (route, customerName) => {
    try {
      const customerRouteData = {
        vehicle:   route.vehicle,
        customer:  customerName,
        deliveries: route.deliveries.filter(d => d.name === customerName),
      };
      const { data } = await api.post('/api/TrackingToken', {
        routeData:    customerRouteData,
        customerName,
      });
      const url = `${window.location.origin}/customer_tracking?token=${data.token}`;
      await navigator.clipboard.writeText(url);
      message.success(`Tracking link for ${customerName} copied to clipboard!`);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.statusText || err?.message || 'Unknown error';
      console.error('[TrackingToken] Error:', err?.response?.status, msg, err);
      message.error(`Failed to generate tracking link: ${msg}`);
    }
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        {[
          { label: 'Total Distance', icon: '📍', value: metrics.totalDistance, change: '↓ 23% less than manual' },
          { label: 'Estimated Time', icon: '⏱️', value: metrics.totalTime, change: '↓ 35% time saved' },
          { label: 'Fuel Cost', icon: '⛽', value: metrics.totalCost, change: '↓ 28% cost reduction' },
          { label: 'Vehicles Used', icon: '🚛', value: metrics.vehiclesUsed, change: metrics.optimization }
        ].map((metric, idx) => (
          <div key={idx} style={{ background: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: 500 }}>{metric.label}</span>
              <div style={{ width: '40px', height: '40px', background: '#ede9fe', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>{metric.icon}</div>
            </div>
            <div style={{ fontSize: '32px', fontWeight: 700, color: '#1f2937', marginBottom: '5px' }}>{metric.value}</div>
            <div style={{ fontSize: '14px', color: '#10b981', fontWeight: 500 }}>{metric.change}</div>
          </div>
        ))}
      </div>

      <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '20px' }}>Live Route Tracking</h3>
        {optimizedRoutes.length > 0 ? (
          <>
            <DashboardMap routes={optimizedRoutes} />
            <div style={{ marginTop: '20px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '10px' }}>Shareable Tracking Links (Expires after delivery)</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px' }}>
                {optimizedRoutes.map((route, routeIndex) => (
                  <div key={routeIndex} style={{ background: '#f9fafb', padding: '15px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <div style={{ marginBottom: '10px' }}>
                      <p style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>{route.vehicle.name} ({route.vehicle.driver})</p>
                      <p style={{ fontSize: '12px', color: '#6b7280', margin: '5px 0 0 0' }}>📦 {route.deliveries.length} stops | ⚖️ {route.totalWeight}kg</p>
                    </div>
                    <div style={{ marginTop: '10px' }}>
                      {route.deliveries.map((delivery, deliveryIndex) => (
                        <div key={deliveryIndex} style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '13px' }}>{delivery.name}</span>
                          <button
                            onClick={() => handleCopyURL(route, delivery.name)}
                            style={{ padding: '6px 10px', background: '#667eea', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                          >
                            🔗 Copy Link
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div style={{ background: '#f9fafb', border: '2px solid #e5e7eb', borderRadius: '12px', height: '600px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="2">
                <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
                <line x1="8" y1="2" x2="8" y2="18"></line>
                <line x1="16" y1="6" x2="16" y2="22"></line>
              </svg>
              <p style={{ marginTop: '15px', fontSize: '16px', color: '#1f2937' }}>Interactive Map View</p>
              <p style={{ fontSize: '14px', marginTop: '5px', color: '#6b7280' }}>Ahmedabad Region - Gujarat</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardTab;
